// The hero animation's choreography, ported from the pinned prototype
// (order-feedback-landing-page/v2 v67). The cue times, typing intervals and
// loop period are the design's, copied rather than re-derived — changing one
// desynchronises it from the CSS transitions it is timed against.
//
// context:
//   One 19s loop, two scenes. The consumer taps thumbs-down, types a comment
//   and submits; the card flies out of the phone; the phone exits as the
//   dashboard enters; the card drops into the dashboard's quote; the merchant's
//   reply types out, a reward attaches, and the send confirms. The prototype
//   drove this off document ids — this port scopes every lookup to the mounted
//   container so the component stays self-contained and testable.

const NOTE =
  'Thanks for letting us know – here are some fries on us to make up for it.'

// Cue times in ms from the start of each loop, and the loop period. Verbatim
// from the prototype.
export const CUES = {
  tapDown: 1200,
  releaseDown: 1600,
  showComment: 1950,
  typeComment: 2550,
  tapSubmit: 4700,
  releaseSubmit: 4980,
  fly: 5200,
  showThanks: 6600,
  exit: 8000,
  drop: 9500,
  typeNote: 11100,
  reward: 14100,
  send: 15100,
  loop: 19000,
} as const

export const COMMENT_TEXT = 'I’m missing my fries'
export const NOTE_TEXT = NOTE
// Per-character typing cadence; the two differ in the prototype.
export const TYPE_MS = { comment: 42, note: 40 } as const

type Handles = { timeouts: number[]; intervals: number[] }

// Drives one mounted hero-animation container. Returns a teardown that cancels
// every pending timer, so a unmount mid-loop leaves nothing running.
export const startHeroTimeline = (
  root: HTMLElement,
  options: { reducedMotion: boolean }
): (() => void) => {
  const q = <T extends HTMLElement>(sel: string): T | null =>
    root.querySelector<T>(sel)

  const stage = q('#hstage')
  const down = q('#cxDown')
  const tap = q('#cxTap')
  const steps = Array.from(root.querySelectorAll<HTMLElement>('.hp-s'))
  const commentText = q('#hpInputTx')
  const submit = q('#hpSubmit')
  const note = q('#hdNote')
  const reward = q('#hdReward')
  const send = q('#hdSend')
  const sent = q('#hdSent')

  // The prototype bails when its two essential nodes are absent; keep that
  // rather than letting a partial copy of the markup throw.
  if (!stage || !note || !send) return () => undefined

  const handles: Handles = { timeouts: [], intervals: [] }
  const at = (ms: number, fn: () => void): void => {
    handles.timeouts.push(window.setTimeout(fn, ms))
  }

  const showStep = (page: number): void => {
    steps.forEach(step =>
      step.classList.toggle('on', step.getAttribute('data-p') === String(page))
    )
  }

  // Types into a node one character at a time, re-appending the caret each
  // tick — the caret is a child span, so it has to be rewritten with the text.
  const typeInto = (
    target: HTMLElement,
    text: string,
    speed: number,
    caretHTML: string,
    onDone?: () => void
  ): void => {
    let i = 0
    target.innerHTML = caretHTML
    const id = window.setInterval(() => {
      i += 1
      target.textContent = text.slice(0, i)
      target.insertAdjacentHTML('beforeend', caretHTML)
      if (i >= text.length) {
        window.clearInterval(id)
        onDone?.()
      }
    }, speed)
    handles.intervals.push(id)
  }

  const clearTimers = (): void => {
    handles.timeouts.forEach(window.clearTimeout)
    handles.intervals.forEach(window.clearInterval)
    handles.timeouts = []
    handles.intervals = []
  }

  const reset = (): void => {
    clearTimers()
    stage.classList.remove('fly', 'exit', 'drop')
    showStep(1)
    down?.classList.remove('tapped')
    tap?.classList.remove('go')
    if (commentText) {
      commentText.className = 'ph'
      commentText.textContent = 'Tell us what happened…'
    }
    submit?.classList.remove('on', 'tapped')
    note.innerHTML = '<span class="hd-cursor"></span>'
    reward?.classList.remove('on')
    send.classList.remove('sent')
    send.textContent = 'Send message'
    sent?.classList.remove('on')
  }

  // The end state, with no motion — what a reduced-motion viewer sees.
  const settle = (): void => {
    showStep(3)
    stage.classList.add('exit', 'drop')
    reward?.classList.add('on')
    note.textContent = NOTE
    send.classList.add('sent')
    send.textContent = 'Sent'
    sent?.classList.add('on')
  }

  if (options.reducedMotion) {
    settle()
    return clearTimers
  }

  const run = (): void => {
    reset()
    at(CUES.tapDown, () => {
      if (tap) {
        tap.classList.remove('go')
        // Force a reflow so the ripple restarts on every loop.
        void tap.offsetWidth
        tap.classList.add('go')
      }
      down?.classList.add('tapped')
    })
    at(CUES.releaseDown, () => down?.classList.remove('tapped'))
    at(CUES.showComment, () => showStep(2))
    at(CUES.typeComment, () => {
      if (!commentText) return
      commentText.className = ''
      typeInto(
        commentText,
        COMMENT_TEXT,
        TYPE_MS.comment,
        '<span class="cur"></span>',
        () => submit?.classList.add('on')
      )
    })
    at(CUES.tapSubmit, () => submit?.classList.add('tapped'))
    at(CUES.releaseSubmit, () => submit?.classList.remove('tapped'))
    at(CUES.fly, () => stage.classList.add('fly'))
    at(CUES.showThanks, () => showStep(3))
    at(CUES.exit, () => stage.classList.add('exit'))
    at(CUES.drop, () => stage.classList.add('drop'))
    at(CUES.typeNote, () =>
      typeInto(note, NOTE, TYPE_MS.note, '<span class="hd-cursor"></span>')
    )
    at(CUES.reward, () => reward?.classList.add('on'))
    at(CUES.send, () => {
      send.classList.add('sent')
      send.textContent = 'Sent'
      sent?.classList.add('on')
    })
  }

  // Only animates while on screen, as the prototype does — an off-screen loop
  // is wasted work on a page the merchant may never scroll.
  let loopId: number | null = null
  const start = (): void => {
    if (loopId !== null) return
    run()
    loopId = window.setInterval(run, CUES.loop)
  }
  const stop = (): void => {
    if (loopId !== null) {
      window.clearInterval(loopId)
      loopId = null
    }
  }

  let observer: IntersectionObserver | null = null
  if (typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver(
      entries =>
        entries.forEach(entry => (entry.isIntersecting ? start() : stop())),
      { threshold: 0.3 }
    )
    observer.observe(stage)
  } else {
    start()
  }

  return () => {
    observer?.disconnect()
    stop()
    clearTimers()
  }
}
